import { useState } from "react";

interface Photo {
  id: string;
  url: string;
  caption?: string;
  fileName?: string;
}

type SafeImageSrc = string | Photo[] | string[] | null | undefined;

interface SafeImageProps {
  src?: SafeImageSrc;
  fallbackText: string;
  className?: string;
  alt?: string;
  onClick?: () => void;
  showMultipleIndicator?: boolean;
}

export default function SafeImage({
  src,
  fallbackText,
  className = "",
  alt = "",
  onClick,
  showMultipleIndicator = true,
}: SafeImageProps) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getImageUrl = (): string | null => {
    if (!src) return null;

    if (typeof src === "string") {
      return src;
    }

    if (Array.isArray(src)) {
      if (src.length > 0) {
        const firstPhoto = src[0];
        if (typeof firstPhoto === "object" && firstPhoto.url) {
          return firstPhoto.url;
        }
        if (typeof firstPhoto === "string") {
          return firstPhoto;
        }
      }
    }

    return null;
  };

  const imageUrl = getImageUrl();

  const getPhotoCount = (): number => {
    if (!src) return 0;

    if (Array.isArray(src)) {
      return src.length;
    }

    return 1; 
  };

  const photoCount = getPhotoCount();

  if (!imageUrl || hasError) {
    const initials = fallbackText
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 3);

    return (
      <div
        className={`flex items-center justify-center bg-gradient-to-br from-brand-400 to-accent-500 text-white font-bold ${className}`}
        onClick={onClick}
      >
        {initials}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} onClick={onClick}>
      {isLoading && (
        <div className="absolute inset-0 bg-slate-200 animate-pulse rounded-lg" />
      )}

      <img
        src={imageUrl}
        alt={alt}
        className={`w-full h-full object-cover rounded-lg ${isLoading ? "opacity-0" : "opacity-100"} transition-opacity duration-300`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setHasError(true);
          setIsLoading(false);
        }}
      />

      {photoCount > 1 && showMultipleIndicator && (
        <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center justify-center">
          <span>{photoCount}</span>
          <svg className="w-3 h-3 ml-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
